import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api` || 'https://theequalizer.ai/api';

// Subscription Plans
const PLANS = [
  {
    id: 'free',
    name: 'Pro Se Starter',
    price: 0,
    interval: 'Forever',
    features: [
      'Basic AI Legal Assistant',
      'Limited Document Templates',
      'Community Support',
      'Basic Legal Resources'
    ]
  },
  {
    id: 'basic',
    name: 'Justice Seeker',
    price: 9.99,
    interval: 'month',
    features: [
      '5 Legal Matters per month',
      'AI Remedy Generator + Template Suite',
      '10+ Downloadable Templates per month',
      'Access to Mailing Room',
      'Printable Affidavits and Templates',
      'Live Chat Support (48 hrs)'
    ]
  },
  {
    id: 'premium',
    name: 'Sovereign Shield',
    price: 99,
    interval: 'month',
    features: [
      'Unlimited Legal Matters',
      'Unlimited Document Generation',
      'Advanced AI Remedy Builder',
      'Unlimited Legal Simulations',
      'Secure Audit Trail',
      'AI Document Review + Suggestions',
      'Access to Sovereign Toolbox',
      'Premium Content Access'
    ]
  },
  {
    id: 'lifetime',
    name: 'Defender of the People',
    price: 1497,
    interval: 'one-time',
    features: [
      'Everything in Sovereign Shield',
      'Lifetime Access (No Monthly Fees)',
      'Priority Support',
      'Exclusive Content Updates',
      'Advanced Training Materials',
      'Direct Access to Legal Experts'
    ]
  }
];

const RegistrationPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('free');
  
  const [formData, setFormData] = useState({
    email: '',
    fullName: '',
    password: '',
    confirmPassword: ''
  });

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.email || !formData.fullName || !formData.password) {
      setError('Please fill in all required fields');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters long');
      return;
    }

    setLoading(true);

    try {
      if (selectedPlan === 'free') {
        // Direct registration for free plan
        await axios.post(`${API_BASE}/auth/register`, {
          email: formData.email,
          password: formData.password,
          full_name: formData.fullName,
          subscription_plan: 'free'
        });

        alert('Registration successful! You can now log in.');
        navigate('/auth');
      } else {
        // Create Stripe checkout session for paid plans
        const response = await axios.post(`${API_BASE}/payment/create-checkout-session`, {
          plan_id: selectedPlan,
          email: formData.email,
          full_name: formData.fullName,
          password: formData.password
        });

        // Redirect to Stripe Checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (err) {
      console.error('Registration error:', err);
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      
      <div className="flex-grow container mx-auto px-4 py-12">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="text-center mb-12">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Create Your Account
            </h1>
            <p className="text-xl text-gray-600">
              Choose your plan and get started with TheEqualizer.AI
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Plan Selection */}
            <div className="lg:col-span-2">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Select Your Plan</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                {PLANS.map((plan) => (
                  <div
                    key={plan.id}
                    onClick={() => setSelectedPlan(plan.id)}
                    className={`cursor-pointer border-2 rounded-lg p-6 transition-all ${
                      selectedPlan === plan.id
                        ? 'border-blue-600 bg-blue-50 shadow-lg'
                        : 'border-gray-200 hover:border-blue-300'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-xl font-bold text-gray-900">{plan.name}</h3>
                      <input
                        type="radio"
                        checked={selectedPlan === plan.id}
                        onChange={() => setSelectedPlan(plan.id)}
                        className="w-5 h-5 text-blue-600"
                      />
                    </div>
                    
                    <div className="mb-4">
                      <span className="text-3xl font-bold text-gray-900">
                        ${plan.price}
                      </span>
                      <span className="text-gray-600 ml-2">
                        {plan.price === 0 ? 'Free' : `/${plan.interval}`}
                      </span>
                    </div>

                    <ul className="space-y-2">
                      {plan.features.slice(0, 3).map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <svg className="w-5 h-5 text-green-500 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          {feature}
                        </li>
                      ))}
                      {plan.features.length > 3 && (
                        <li className="text-sm text-gray-500 ml-7">
                          +{plan.features.length - 3} more features
                        </li>
                      )}
                    </ul>
                  </div>
                ))}
              </div>
            </div>

            {/* Registration Form */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-md p-8 sticky top-8">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Your Information</h2>
                
                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Password *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      minLength={8}
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Minimum 8 characters</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Confirm Password *
                    </label>
                    <input
                      type="password"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      required
                    />
                  </div>

                  {/* Selected Plan Summary */}
                  <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-700">Selected Plan:</span>
                      <span className="text-sm font-bold text-gray-900">
                        {PLANS.find(p => p.id === selectedPlan)?.name}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm font-medium text-gray-700">Total:</span>
                      <span className="text-lg font-bold text-blue-600">
                        ${PLANS.find(p => p.id === selectedPlan)?.price}
                      </span>
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-blue-600 text-white py-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold text-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {loading ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin h-5 w-5 mr-3" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Processing...
                      </span>
                    ) : selectedPlan === 'free' ? (
                      'Create Free Account'
                    ) : (
                      'Continue to Payment'
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center mt-4">
                    Already have an account?{' '}
                    <button
                      type="button"
                      onClick={() => navigate('/auth')}
                      className="text-blue-600 hover:underline"
                    >
                      Log in
                    </button>
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default RegistrationPage;
